<script setup>
import {computed, watch, ref} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import _ from 'lodash';
import {DateTime} from 'luxon';

const props = defineProps({
  user: {
    type: Object,
    default: null
  },
  club: {
    type: Object,
    default: null
  },
  league: {
    type: Object,
    default: null
  },
  ladder: {
    type: Object,
    default: null
  },
  ladderChallenge: {
    type: Object,
    default: null
  },
  fixture: {
    type: Object,
    default: null
  },
  tournament: {
    type: Object,
    default: null
  },
  meet: {
    type: Object,
    default: null
  },
  // the occurrence (date) when on a meet event page — adds the trailing crumb
  meetOccurrence: {
    type: Object,
    default: null
  },
  americano: {
    type: Object,
    default: null
  },
  // the cycle when viewing a specific americano cycle — adds the trailing crumb
  americanoCycle: {
    type: Object,
    default: null
  },
  howTo: {
    type: Object,
    default: null
  }
});

const router = useRouter();
const route = useRoute();
const breadcrumbs = ref([]);
const genBreadcrumbs = () => {
  const routes = router.getRoutes();
  const breadcrumbs = [
    {
      title: 'Home',
      disabled: route.name === 'Home',
      to: '/',
    },
  ];

  if (_.some(route.matched, r => _.includes(['Contact'], r.name))) {
    breadcrumbs.push({title: "Contact", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['ClubSearch'], r.name))) {
    breadcrumbs.push({title: "Search clubs", disabled: true});
  }

  if (_.some(route.matched, r => _.includes(['HowToView', 'HowItWorks'], r.name)) && props.howTo) {
    breadcrumbs.push({title: "Help", disabled: false, to: {name: 'HowItWorks'}});
  }
  if (_.some(route.matched, r => _.includes(['HowToView'], r.name)) && props.howTo) {
    breadcrumbs.push({title: props.howTo.title, disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['HowItWorksCategories'], r.name))) {
    breadcrumbs.push({title: "Categories", disabled: route.name === 'HowItWorksCategories', to: {name: 'HowItWorks'}});
  }
  if (_.some(route.matched, r => _.includes(['HowToCategoryAdd', 'HowToCategoryEdit', 'HowToCategoryDelete'], r.name))) {
    breadcrumbs.push({
      title: route.name === 'HowToCategoryEdit' ? 'Edit' : route.name === 'HowToCategoryDelete' ? 'Delete' : 'Add new',
      disabled: true
    });
  }
  if (_.some(route.matched, r => _.includes(['HowToAdd', 'HowToEdit', 'HowToDelete'], r.name))) {
    breadcrumbs.push({
      title: route.name === 'HowToEdit' ? 'Edit' : route.name === 'HowToDelete' ? 'Delete' : 'Add new',
      disabled: true
    });
  }
  if (_.some(route.matched, r => _.includes(['HowItWorks'], r.name))) {
    breadcrumbs.push({title: "Help", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['About'], r.name))) {
    breadcrumbs.push({title: "About", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['YourProfile'], r.name))) {
    breadcrumbs.push({title: "Your profile", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['PlayerDetail'], r.name) && props.user)) {
    breadcrumbs.push({title: props.user.fullName, disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['PlayerStats'], r.name) && props.user)) {
    breadcrumbs.push({
      title: props.user.fullName,
      disabled: false,
      to: {name: 'PlayerDetail', params: {playerId: props.user.id}}
    });
    breadcrumbs.push({title: 'Stats', disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['FixtureSearch'], r.name))) {
    breadcrumbs.push({title: 'Match search', disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['ClubDetail'], r.name)) && props.club) {
    let routePath = routes.find(r => r.name === 'ClubDetail').path;
    routePath = routePath.replace(":clubId", props.club.slug ?? props.club.id);
    breadcrumbs.push({title: props.club.name, disabled: route.name === 'ClubDetail', to: routePath});
  }
  if (_.some(route.matched, r => _.includes(['ClubStats'], r.name)) && props.club) {
    let routePath = routes.find(r => r.name === 'ClubDetail').path;
    routePath = routePath.replace(":clubId", props.club.slug ?? props.club.id);
    breadcrumbs.push({title: props.club.name, disabled: route.name === 'ClubDetail', to: routePath});
    breadcrumbs.push({title: 'Stats', disabled: true});
  }

  if (_.some(route.matched, r => _.includes(['LeagueDetail', 'LeagueCycleStats'], r.name)) && props.league) {
    if (props.league.clubId && props.league.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.league.clubSlug);
      breadcrumbs.push({title: props.league.clubName, disabled: route.name === 'ClubDetail', to: clubRoute});
    }
    let leagueRoute = routes.find(r => r.name === 'LeagueDetail').path;
    leagueRoute = leagueRoute.replace(":leagueId", props.league.id);
    breadcrumbs.push({title: props.league.name, disabled: route.name === 'LeagueDetail', to: leagueRoute});
    if (route.name === 'LeagueCycleStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
  }
  if (_.some(route.matched, r => _.includes(['LadderDetail', 'LadderStats'], r.name)) && props.ladder) {
    if (props.ladder.clubId && props.ladder.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.ladder.clubSlug);
      breadcrumbs.push({title: props.ladder.clubName, disabled: route.name === 'ClubDetail', to: clubRoute});
    }
    let ladderRoute = routes.find(r => r.name === 'LadderDetail').path;
    ladderRoute = ladderRoute.replace(":ladderId", props.ladder.id);
    breadcrumbs.push({title: props.ladder.name, disabled: route.name === 'LadderDetail', to: ladderRoute});
    if (route.name === 'LadderStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
  }
  if (_.some(route.matched, r => _.includes(['LadderChallengeDetail'], r.name)) && props.ladderChallenge) {
    if (props.ladderChallenge.clubId && props.ladderChallenge.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.ladderChallenge.clubSlug);
      breadcrumbs.push({title: props.ladderChallenge.clubName, disabled: route.name === 'ClubDetail', to: clubRoute});
    }
    let ladderRoute = routes.find(r => r.name === 'LadderDetail').path;
    ladderRoute = ladderRoute.replace(":ladderId", props.ladderChallenge.ladderId);
    breadcrumbs.push({title: props.ladderChallenge.ladderName, disabled: false, to: ladderRoute});
    breadcrumbs.push({title: "Challenge", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['TournamentDetail'], r.name)) && props.tournament) {
    if (props.tournament.clubId && props.tournament.club?.name) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.tournament.club.slug);
      breadcrumbs.push({title: props.tournament.club.name, disabled: false, to: clubRoute});
    }
    let tournamentRoute = routes.find(r => r.name === 'TournamentDetail').path;
    tournamentRoute = tournamentRoute.replace(":tournamentId", props.tournament.id);
    breadcrumbs.push({title: props.tournament.name, disabled: route.name === 'TournamentDetail', to: tournamentRoute});
  }
  if (_.some(route.matched, r => _.includes(['MeetDetail', 'MeetStats', 'MeetOccurrenceDetail'], r.name)) && props.meet) {
    if (props.meet.clubId && props.meet.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.meet.clubSlug ?? props.meet.clubId);
      breadcrumbs.push({title: props.meet.clubName, disabled: false, to: clubRoute});
    }
    let meetRoute = routes.find(r => r.name === 'MeetDetail').path;
    meetRoute = meetRoute.replace(":meetId", props.meet.id);
    breadcrumbs.push({title: props.meet.name, disabled: route.name === 'MeetDetail', to: meetRoute});
    if (route.name === 'MeetStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
    if (route.name === 'MeetOccurrenceDetail' && props.meetOccurrence) {
      breadcrumbs.push({
        title: DateTime.fromISO(props.meetOccurrence.startsAt, {zone: 'utc'}).toLocal().toFormat('ccc d LLL'),
        disabled: true
      });
    }
  }
  if (_.some(route.matched, r => _.includes(['AmericanoDetail', 'AmericanoStats'], r.name)) && props.americano) {
    if (props.americano.clubId && props.americano.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.americano.clubSlug ?? props.americano.clubId);
      breadcrumbs.push({title: props.americano.clubName, disabled: false, to: clubRoute});
    }
    let americanoRoute = routes.find(r => r.name === 'AmericanoDetail').path;
    americanoRoute = americanoRoute.replace(":americanoId", props.americano.id);
    americanoRoute = americanoRoute.replace("/:cycleId?", "");
    breadcrumbs.push({
      title: props.americano.name,
      disabled: route.name === 'AmericanoDetail' && !props.americanoCycle,
      to: americanoRoute
    });
    if (route.name === 'AmericanoStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
    if (route.name === 'AmericanoDetail' && props.americanoCycle) {
      breadcrumbs.push({title: props.americanoCycle.name, disabled: true});
    }
  }
  if (_.some(route.matched, r => _.includes(['FixtureDetail'], r.name)) && props.fixture) {
    // Add club breadcrumb
    if (props.fixture.clubId && props.fixture.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.fixture.clubSlug);
      breadcrumbs.push({title: props.fixture.clubName, disabled: false, to: clubRoute});
    }

    // Add ladder or league breadcrumb
    if (props.fixture.ladderId && props.fixture.ladderName) {
      // Ladder fixture
      let ladderRoute = routes.find(r => r.name === 'LadderDetail').path;
      ladderRoute = ladderRoute.replace(":ladderId", props.fixture.ladderId);
      breadcrumbs.push({title: props.fixture.ladderName, disabled: false, to: ladderRoute});
    } else if (props.fixture.leagueCycleDivisionId && props.fixture.leagueCycleDivisionName) {
      // League fixture
      let leagueRoute = routes.find(r => r.name === 'LeagueDetail').path;
      leagueRoute = leagueRoute.replace(":leagueId", props.fixture.leagueId);
      leagueRoute = leagueRoute.replace(":cycleId?", props.fixture.leagueCycleId || '');
      breadcrumbs.push({
        title: props.fixture.leagueName,
        disabled: false,
        to: {
          path: leagueRoute,
          hash: `#division-${props.fixture.leagueCycleDivisionId}`
        }
      });

    } else if (props.fixture.americanoCycleDivisionId && props.fixture.americanoName) {
      // Americano match — link back to the americano league's cycle
      breadcrumbs.push({
        title: props.fixture.americanoName,
        disabled: false,
        to: {
          name: 'AmericanoDetail',
          params: {americanoId: props.fixture.americanoId, cycleId: props.fixture.americanoCycleId}
        }
      });
    } else if (props.fixture.meetOccurrenceId && props.fixture.meetId) {
      // Meet match — the meet, then its date (a meet match is scheduled ON the date)
      breadcrumbs.push({
        title: props.fixture.meetName || 'Meet',
        disabled: false,
        to: {name: 'MeetDetail', params: {meetId: props.fixture.meetId}}
      });
      breadcrumbs.push({
        title: props.fixture.scheduledAt
            ? DateTime.fromISO(props.fixture.scheduledAt, {zone: 'utc'}).toLocal().toFormat('ccc d LLL')
            : 'Date',
        disabled: false,
        to: {name: 'MeetOccurrenceDetail', params: {meetId: props.fixture.meetId, occurrenceId: props.fixture.meetOccurrenceId}}
      });
    }

    // Add fixture breadcrumb
    breadcrumbs.push({title: "Match", disabled: true});
  }

  return breadcrumbs;
}
watch(() => route, () => {
  breadcrumbs.value = genBreadcrumbs();
}, {immediate: true});

</script>

<template>
  <!-- redesign breadcrumbs: subtle text links + chevron separators (mockup .breadcrumbs) -->
  <nav class="tl-breadcrumbs" aria-label="Breadcrumb">
    <template v-for="(item, i) in breadcrumbs" :key="i">
      <span v-if="i > 0" class="sep"><v-icon size="14">mdi-chevron-right</v-icon></span>
      <nuxt-link v-if="item.to && !item.disabled" :to="item.to" class="crumb">{{ item.title }}</nuxt-link>
      <span v-else class="crumb here">{{ item.title }}</span>
    </template>
  </nav>
</template>

<style scoped lang="scss">
.tl-breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 500;
  margin-bottom: 14px;
}

.crumb {
  color: rgb(var(--v-theme-textSubtle));
  text-decoration: none;
  white-space: nowrap;
}

a.crumb:hover {
  color: rgb(var(--v-theme-charcoal));
}

.crumb.here {
  color: rgb(var(--v-theme-charcoal));
}

.sep {
  display: inline-flex;
  align-items: center;
  color: rgb(var(--v-theme-borderLight));
}
</style>
